import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import Button from '../../../components/buttons/Button';
import Dropdown from '../../../components/inputFields/DropDown';
import MultiSelectDropDown from '../../../components/inputFields/MultiSelectDropDown';
import Breadcrumbs from '../../../components/BreadCrumbs';
import { FiUpload, FiDownload, FiFileText } from 'react-icons/fi';
import { getB2BCampaigns, uploadB2BLeads } from '../../../services/b2bLeads';
import { districtApi } from '../../../constants/apis';
import axios from 'axios';
import { categoriesCRUDUrl } from '../../../constants/apis';
import { getUserToken } from '../../../helpers/local_storage_helpers';

const B2BLeadUpload = ({ isModal = false, onUploadSuccess }) => {
    const [campaigns, setCampaigns] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);

    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [selectedDistrict, setSelectedDistrict] = useState(null);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [file, setFile] = useState(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        fetchResources();
    }, []);

    const fetchResources = async () => {
        setLoading(true);
        try {
            const [campRes, catRes] = await Promise.all([
                getB2BCampaigns(),
                axios.get(categoriesCRUDUrl, {
                    headers: { Authorization: `Token ${getUserToken()}` }
                })
            ]);

            if (campRes.status === 200) setCampaigns(campRes.data.results || campRes.data);
            if (catRes.status === 200) setCategories(catRes.data.results || catRes.data);

            // Fetch districts (Generic RATLAM for now or list all - usually requires state)
            // For now, let's fetch all districts if possible or keep a placeholder
            // District API usually expects a state. Let's assume MP (Madhya Pradesh) as default
            const distRes = await axios.get(`${districtApi}Madhya Pradesh`, {
                headers: { Authorization: `Token ${localStorage.getItem("token")}` }
            });
            if (distRes.status === 200) setDistricts(distRes.data);

        } catch (error) {
            console.error("Error fetching resources:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            const fileName = selectedFile.name.toLowerCase();
            if (fileName.endsWith('.csv') || fileName.endsWith('.xls') || fileName.endsWith('.xlsx')) {
                setFile(selectedFile);
            } else {
                toast.error("Please select a valid CSV or Excel file");
            }
        }
    };

    const handleUpload = async () => {
        if (!file) {
            toast.error("Please select a file first");
            return;
        }

        const formData = new FormData();
        formData.append('file', file);
        if (selectedCampaign) formData.append('campaign', selectedCampaign);
        if (selectedDistrict) formData.append('district', selectedDistrict);
        if (selectedCategories && Array.isArray(selectedCategories) && selectedCategories.length > 0) {
            selectedCategories.forEach(cat => formData.append('product_category', cat));
        }

        setUploading(true);
        try {
            const res = await uploadB2BLeads(formData);
            if (res.status === 200 || res.status === 201) {
                // Trigger download of the Excel report
                const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `b2b_upload_report_${new Date().getTime()}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                window.URL.revokeObjectURL(url);

                toast.success("Leads processed! Opening report...");
                setFile(null);
                setSelectedCampaign(null);
                setSelectedDistrict(null);
                setSelectedCategories([]);
                if (fileInputRef.current) fileInputRef.current.value = '';
                if (onUploadSuccess) onUploadSuccess();
            } else {
                // Handle error when responseType is blob
                if (res.data instanceof Blob) {
                    const text = await res.data.text();
                    try {
                        const errorData = JSON.parse(text);
                        toast.error(errorData.detail || "Upload failed");
                    } catch (e) {
                        toast.error("Server error during upload");
                    }
                } else {
                    toast.error(res.data?.detail || "Upload failed");
                }
            }
        } catch (error) {
            toast.error("An error occurred during upload");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className={isModal ? "" : "space-y-6"}>
            {!isModal && (
                <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-6 border border-orange-100">
                    <Breadcrumbs items={[{ name: 'Home', link: '/dashboard' }, { name: 'Leads', link: '/leads-management' }, { name: 'B2B' }, { name: 'Upload' }]} />
                    <h1 className="text-2xl font-bold text-gray-800 mt-2">B2B Leads Upload</h1>
                    <p className="text-gray-600 mt-1">Bulk import leads from CSV/Excel file with custom attributes</p>
                </div>
            )}

            <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Configuration Section */}
                        <div className="space-y-6">
                            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                                <span className="bg-orange-100 p-2 rounded-lg text-orange-600">1</span>
                                Configure Upload
                            </h2>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Campaign (Optional)</label>
                                <Dropdown
                                    placeholder="Select Campaign"
                                    options={campaigns.map(c => ({ label: c.name, value: c.id }))}
                                    value={selectedCampaign}
                                    onChange={(val) => setSelectedCampaign(val)}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">District (Optional)</label>
                                <Dropdown
                                    placeholder="Select District"
                                    options={districts.map(d => ({ label: d.name, value: d.id }))}
                                    value={selectedDistrict}
                                    needSearch={true}
                                    onChange={(val) => setSelectedDistrict(val)}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Product Categories</label>
                                <MultiSelectDropDown
                                    placeholder="Select Categories"
                                    options={categories.map(c => ({ label: c.name, value: c.id }))}
                                    value={selectedCategories}
                                    onChange={(val) => setSelectedCategories(val)}
                                />
                            </div>
                        </div>

                        {/* File Section */}
                        <div className="space-y-6">
                            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                                <span className="bg-blue-100 p-2 rounded-lg text-blue-600">2</span>
                                Select File
                            </h2>

                            <div
                                onClick={() => fileInputRef.current.click()}
                                className={`border-2 border-dashed rounded-xl p-10 flex flex-col items-center justify-center cursor-pointer transition-all ${file ? 'border-primary bg-green-50' : 'border-gray-200 hover:border-primary hover:bg-gray-50'}`}
                            >
                                {file ? (
                                    <div className="text-center">
                                        <FiFileText className="text-primary w-12 h-12 mx-auto mb-2" />
                                        <p className="text-sm font-bold text-gray-800">{file.name}</p>
                                        <p className="text-xs text-gray-500 mt-1">{(file.size / 1024).toFixed(2)} KB</p>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                                            className="text-red-500 text-xs mt-3 underline"
                                        >
                                            Change File
                                        </button>
                                    </div>
                                ) : (
                                    <div className="text-center">
                                        <FiUpload className="text-gray-400 w-12 h-12 mx-auto mb-4" />
                                        <p className="text-sm font-medium text-gray-600">Click to upload or drag & drop</p>
                                        <p className="text-xs text-gray-400 mt-2">CSV or Excel files only</p>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    className="hidden"
                                    accept=".csv, .xls, .xlsx, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                    onChange={handleFileChange}
                                />
                            </div>

                            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                                <div className="flex items-start gap-3">
                                    <FiDownload className="text-blue-500 mt-1" />
                                    <div>
                                        <h4 className="text-sm font-bold text-blue-900">Need a template?</h4>
                                        <p className="text-xs text-blue-700 mt-1">Download the sample CSV file to ensure your data is formatted correctly.</p>
                                        <button className="text-xs font-bold text-blue-600 mt-2 hover:underline">Download Template.csv</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-10 pt-8 border-t border-gray-100 flex justify-end gap-3">
                        <Button
                            variant="primary"
                            onClick={handleUpload}
                            loading={uploading}
                            disabled={uploading || !file}
                            className="bg-primary text-white px-10 py-3 rounded-lg shadow-lg hover:shadow-xl transition-all"
                        >
                            Start Import
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default B2BLeadUpload;
